import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal, IconButton } from "@mui/material";
import { IoArrowBack } from "react-icons/io5";
import { MdCategory, MdSearch } from "react-icons/md";
import { FaPlus, FaCheck, FaTrash } from "react-icons/fa";
import Spinner from "../spinner";
import { debounce } from "lodash";

function CategoryManagement({ 
  open, 
  onClose, 
  selectedProduct,
  allCategories,
  mappedCategories,
  categoriesLoading,
  isAddingCategory,
  isRemovingCategory,
  isCategoryMapped,
  getMappedCategory,
  handleAddCategory,
  handleRemoveCategory,
  handleSearchCategories,
  searchQuery,
}) {
  // Local state for search input
  const [localSearch, setLocalSearch] = useState("");
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      handleSearchCategories(value);
    }, 300),
    [handleSearchCategories]
  );
  
  // Handle search input change with lodash debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedSearch(value);
  };
  
  // Clear the debounce when component unmounts
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);
  
  // Reset local search when modal closes or opens
  useEffect(() => {
    if (open) {
      setLocalSearch(searchQuery || "");
    } else {
      setLocalSearch("");
    }
  }, [open, searchQuery]);
  
  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      disableEscapeKeyDown={true}
      aria-labelledby="category-modal-title"
      aria-describedby="category-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div className="w-full sm:w-11/12 md:w-10/12 lg:w-9/12 xl:w-8/12 max-w-5xl mx-auto bg-white rounded-md flex flex-col max-h-[90vh]">
        <div className="head-modal py-3 px-4 sm:px-6 border-b">
          <div className="flex items-center space-x-4">
            <div
              className="w-7 h-7 cursor-pointer"
              onClick={onClose}
            >
              <IoArrowBack className="w-full h-full" />
            </div>
            <h2 className="heading text-xl sm:text-2xl truncate">
              {selectedProduct?.name ? `Manage Categories for '${selectedProduct.name}'` : 'Manage Categories'}
            </h2>
          </div>
        </div>
        
        <div className="body-modal py-4 px-4 sm:px-6 flex-1 overflow-y-auto">
          {/* Search bar for categories */}
          <div className="mb-4 relative">
            <div className="relative">
              <input
                type="text"
                placeholder="Search categories..."
                value={localSearch}
                onChange={handleSearchChange}
                className="input pr-10 w-full"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                <MdSearch size={20} />
              </div>
            </div>
          </div>
          
          {/* Show loader while categories are loading or mutations are in progress */}
          {(categoriesLoading || isAddingCategory || isRemovingCategory) ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <>
              {allCategories.length === 0 ? (
                <div className="text-center py-8">
                  <MdCategory className="w-12 h-12 mx-auto text-gray-400" />
                  <p className="mt-2 text-gray-500">
                    {searchQuery ? 'No categories found matching your search' : 'No categories available'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold text-sm">Category</th>
                        <th className="text-right py-3 px-4 font-semibold text-sm">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allCategories.map((category) => {
                        const isAlreadyMapped = isCategoryMapped(category.category_id);
                        const mappedCategory = getMappedCategory(category.category_id);
                        
                        return (
                          <tr 
                            key={category.category_id} 
                            className={`border-b transition-all duration-200 ${isAlreadyMapped ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-50'}`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center">
                                {isAlreadyMapped && (
                                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center mr-3 flex-shrink-0">
                                    <FaCheck className="text-white text-xs" />
                                  </div>
                                )}
                                <span className={`font-medium ${isAlreadyMapped ? 'text-green-700' : ''}`}>
                                  {category.name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right">
                              {isAlreadyMapped ? (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<FaTrash size={14} />}
                                  onClick={() => handleRemoveCategory(mappedCategory.product_category_map_id)}
                                >
                                  Remove
                                </Button>
                              ) : (
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  startIcon={<FaPlus />}
                                  onClick={() => handleAddCategory(category.category_id)}
                                >
                                  Add
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="foot-modal py-4 text-center border-t">
          <Button
            variant="contained"
            className="primaryBtn-contained px-8 sm:px-10"
            onClick={onClose}
            disabled={categoriesLoading || isAddingCategory || isRemovingCategory}
          >
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default CategoryManagement;
